/**
 * Screen-local fetch module for the route-rules screen (epic 006 decision #13
 * / A2 b): `lib/api.ts` is never edited, so this screen owns its endpoints —
 * the same three verbs task 09's `/admin/route-rules` controller serves. The
 * envelope/error handling below mirrors `screens/partner-credentials/api.ts`
 * line for line (both copy `lib/api.ts`'s private `request()`, which is not
 * exported). Wire types come from `@leads-platform/shared`; there is no local
 * type mirror.
 */

import type { RouteRuleRowResponse, UpsertRouteRuleInput } from '@leads-platform/shared';
import { apiFetch } from '../../lib/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/**
 * Error twin of `lib/api.ts`'s `ApiError` — same `{ status, message,
 * validationErrors }` shape, so screens catch this module's errors exactly as
 * they catch the shared client's.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public validationErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Map a backend validation error array (`{path, message, code}`) into a
 * `Record<path, message[]>` for per-field surfacing. The root path is
 * normalized to `_form`. Verbatim from `lib/api.ts`.
 */
function mapValidationErrors(errors: unknown): Record<string, string[]> | undefined {
  if (!Array.isArray(errors)) return undefined;
  const out: Record<string, string[]> = {};
  for (const entry of errors) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as { path?: string; message?: string };
    const key = e.path && e.path.length > 0 ? e.path : '_form';
    const msg = e.message ?? 'Invalid';
    if (!out[key]) out[key] = [];
    out[key].push(msg);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** API response envelope — matches the backend's ApiResponse<T> shape. */
interface ApiResponse<T> {
  data: T;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(`${API_BASE_URL}${ADMIN_PREFIX}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      typeof errorData.message === 'string' && errorData.message.length > 0
        ? errorData.message
        : response.statusText;
    throw new ApiError(response.status, message, mapValidationErrors(errorData.errors));
  }

  const text = await response.text();
  if (text.length === 0) {
    throw new ApiError(0, 'Empty response from the API');
  }
  const envelope: ApiResponse<T> = JSON.parse(text);
  return envelope.data;
}

/** Every rule, ordered by owning-campaign name (the service's `list()`). */
export function getRouteRules(): Promise<RouteRuleRowResponse[]> {
  return request<RouteRuleRowResponse[]>('/route-rules');
}

/**
 * Create or update the single rule a campaign carries — the campaign id rides
 * the path (`PUT /admin/route-rules/:campaignId`), the body is the shared
 * upsert input (`destinationCampaignId` as a string, the `<select>` value;
 * `UpsertRouteRuleInput` is `z.input`, the wire side of the schema).
 */
export function upsertRouteRule(
  campaignId: string,
  body: UpsertRouteRuleInput
): Promise<RouteRuleRowResponse> {
  return request<RouteRuleRowResponse>(`/route-rules/${campaignId}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

/** Drop the campaign's rule; the api answers 200 with `{ data: null }`. */
export function deleteRouteRule(campaignId: string): Promise<null> {
  return request<null>(`/route-rules/${campaignId}`, { method: 'DELETE' });
}
